import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { GridColDef } from "@mui/x-data-grid-premium";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { useMemo, type FC } from "react";
import {
  PersonIcon3,
  RegistrationControllerIcon2,
  RegistrationProcessorIcon2,
  DpiaIcon3,
  LayoutDashboardIcon,
  TenantIcon3,
} from "../../shared/icons";
import {
  prefetchRegistrationById,
  prefetchRegistrationForm,
  prefetchRegistrationsGridView,
  prefetchRegistrationUsersWithAccessCount,
  customerRegistrationFormQueryOptions,
  useGetRegistrationForm,
  useGetRegistrationsGridView,
  useUpdateRegistrationsGridViewStringifiedFilters,
} from "../../api/registrations";
import { useQuery } from "@tanstack/react-query";
import { DynamicGrid } from "../../dynamicGrid";
import { useGetActiveToolbarTabValue } from "../../dynamicGrid/Toolbar";
import { unionForms } from "../../enterpriseCustomer/unionForms";
import { scopeRegistrationsToController, useControllerFilter } from "./ControllerFilter";
import { DynamicShared } from "../../dynamicShared";
import { ConnectedTenantEntry, TenantControllerName, connectedTenantsText } from "../../shared/ConnectedTenantsList";
import {
  MANAGED_BY_FIELD,
  REGISTRATION_STATUS_VARIANTS,
  type GridViewRegistration,
} from "../../api/registrations.types";
import { systemsQueryOptions } from "../../api/systems";
import { useGetUsersFromCurrentOrganisation } from "../../api/users";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { usePrivacyDocumentTitle } from "../../shared/usePrivacyDocumentTitle";
import { EmptyState } from "../../shared/EmptyState";
import { useGetDpias } from "../../api/dpia";
import { useCurrentCustomer, useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";
import { customerOrganisationNames } from "../../enterpriseCustomer/enterpriseCustomer";
import { HoverPopover, useHoverPopover } from "../../shared/HoverPopover";
import { usePermission } from "../../shared/permissions";
import { personalDataControllersDisplayListQueryOptions } from "../../api/personalDataControllers";
import { useGetTenantsHierarchyList } from "../../api/tenants";
import { trackEvent } from "../../shared/Snowplow";
import { selectPartnerOptionsQueryOptions } from "../../api/partners";
import {
  REGISTRATION_METADATA_FIELDS,
  REGISTRATION_PROCESSOR_METADATA_FIELDS,
  processorOf,
  type ConnectedTenant,
} from "../../api/registrations.types";
import { RegistrationToolbar } from "./RegistrationGridToolbar";
import { responsibleColumn } from "../../shared/responsibleColumn";
import { statusColumn } from "../../shared/statusColumn";
import { sharedProcessingStatusNote } from "../../enterpriseCustomer/sharedProcessingStatusNote";
import type { RegistrationVariant } from "./RegistrationVariantProvider";
import { RegistrationVariantProvider, useRegistrationVariant } from "./RegistrationVariantProvider";
import { RegistrationSidePanel } from "./RegistrationSidePanel/RegistrationSidePanel";
import { RISK_TAGS } from "./RegistrationSidePanel/RisksFactors";
import { RegistrationInlineFormDrawer, RegistrationMultiStepFormDrawer } from "./RegistrationDrawer/RegistrationDrawer";
import { DpiaRecommendedSubDrawer } from "./RegistrationDrawer/DpiaRecommendedSubDrawer";
import { UsersWithAccessSubDrawer } from "./RegistrationDrawer/UserWithAccessSubDrawer";
import { RegistrationHistoryDrawer } from "./RegistrationDrawer/RegistrationHistoryDrawer";
import { RegistrationActionMenu } from "./RegistrationDrawer/RegistrationDrawerHeader";
import {
  CustomerManagedRegistrationDrawer,
  OrCustomerManaged,
  RedirectToOverview,
} from "../../enterpriseCustomer/CustomerManagedRegistrationDrawer";

const RegistrationsGridPage: FC<{ variant: RegistrationVariant }> = ({ variant }) => {
  return (
    <RegistrationVariantProvider variant={variant}>
      <RegistrationsGridPageWithVariant />
    </RegistrationVariantProvider>
  );
};

type SourceTab = "all" | "own" | "library";
const SOURCE_QUERY_KEY = "source";
const SOURCE_TABS: Array<{ labelKey: TranslationKey; value: SourceTab }> = [
  { labelKey: "RegistrationGrid.SourceTabs.All", value: "all" },
  { labelKey: "RegistrationGrid.SourceTabs.Own", value: "own" },
  { labelKey: "RegistrationGrid.SourceTabs.Library", value: "library" },
];

const RegistrationsGridPageWithVariant = () => {
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const currentOrganisation = useCurrentOrganisation();
  const currentCustomer = useCurrentCustomer();
  usePrivacyDocumentTitle(`RegistrationGrid.${variant}.Title`);
  const registrationsGridView = useGetRegistrationsGridView(variant);
  const registrationsForm = useGetRegistrationForm(variant);
  const isEnterpriseController = variant === "Controller" && currentCustomer.customerKind === "enterprise";
  const customerFormQuery = useQuery({ ...customerRegistrationFormQueryOptions(), enabled: isEnterpriseController });
  const sourceTab = useGetActiveToolbarTabValue<SourceTab>().getActiveTabValue(SOURCE_QUERY_KEY, SOURCE_TABS);
  const gridForm = !isEnterpriseController
    ? registrationsForm.data
    : sourceTab === "own"
      ? registrationsForm.data
      : sourceTab === "library"
        ? customerFormQuery.data
        : registrationsForm.data &&
          customerFormQuery.data &&
          unionForms(registrationsForm.data, customerFormQuery.data);
  const { mutate: updateStringifiedFilters } = useUpdateRegistrationsGridViewStringifiedFilters();
  const controllerFilter = useControllerFilter(registrationsGridView.data?.stringifiedFilters);

  const registrations = registrationsGridView.data?.registrations;

  const visibleRegistrations = useMemo(
    () => registrations && scopeRegistrationsToController(registrations, controllerFilter.selectedControllerId),
    [registrations, controllerFilter.selectedControllerId]
  );
  const systemsQueryResult = useQuery(systemsQueryOptions());
  const dpiasQueryResult = useGetDpias({ enabled: currentOrganisation.dpiaEnabled });
  const usersQueryResult = useGetUsersFromCurrentOrganisation();
  const multiTenantsQueryResult = useGetTenantsHierarchyList();
  const personalDataControllersQueryResult = useQuery(personalDataControllersDisplayListQueryOptions());
  const partnersQueryResult = useQuery(selectPartnerOptionsQueryOptions());
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Grid
      rowsData={visibleRegistrations}
      form={gridForm}
      getRowId={getRowId}
      asyncQuestionCardData={useMemo(
        () => ({
          systemsQueryData: systemsQueryResult.data,
          usersQueryData: usersQueryResult.data,
          dpiasQueryData: dpiasQueryResult.data,
          personalDataControllersQueryData: personalDataControllersQueryResult.data,
          multiTenantsQueryData: multiTenantsQueryResult.data,
          partnersQueryData: partnersQueryResult.data,
          enterpriseCustomerOrganisationsQueryData: customerOrganisationNames(currentCustomer),
        }),
        [
          systemsQueryResult.data,
          usersQueryResult.data,
          dpiasQueryResult.data,
          personalDataControllersQueryResult.data,
          multiTenantsQueryResult.data,
          partnersQueryResult.data,
          currentCustomer,
        ]
      )}
      savedFiltersProps={{
        stringifiedFilters: registrationsGridView.data?.stringifiedFilters,
        extraState: { controllerId: controllerFilter.selectedControllerId },
        onReset: controllerFilter.followSavedView,
        onChange: stringifiedFilters => {
          controllerFilter.onSavedFiltersChange(stringifiedFilters);
          trackEvent.changeRegistrationSavedFilters();
          updateStringifiedFilters(stringifiedFilters);
        },
      }}
      toolbarTabs={
        isEnterpriseController
          ? {
              queryKey: SOURCE_QUERY_KEY,
              sizeToText: true,
              filter: (row, activeTab) => activeTab === "all" || (activeTab === "library") === Boolean(row.managedBy),
              tabs: SOURCE_TABS,
            }
          : undefined
      }
      onRowSelect={id => navigate(paths.registrationsInline({ id, variant }))}
      defaultSort={{ field: REGISTRATION_METADATA_FIELDS.UPDATED_AT, sort: "desc" }}
      Toolbar={RegistrationToolbar}
      Footer={RegistrationGridFooter}
      mainColumnProps={{
        headerName: t("RegistrationGrid.NameHeader"),
        renderLabel: row => row.name,
        renderIcon: ({ iconProps }) => {
          const Icon = variant === "Controller" ? RegistrationControllerIcon2 : RegistrationProcessorIcon2;
          return <Icon {...iconProps} />;
        },
      }}
      getColumns={questionCardColumns => [
        ...(isEnterpriseController
          ? [
              managedByColumn({
                headerName: t("RegistrationGrid.ManagedByHeader"),
                organisationName: currentOrganisation.name,
              }),
            ]
          : []),
        statusColumn(t, isEnterpriseController ? sharedProcessingStatusNote(t) : undefined),
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.CreatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.CREATED_AT,
        }),
        DynamicGrid.dateTimeColumn({
          headerName: t("RegistrationGrid.UpdatedAtHeader"),
          field: REGISTRATION_METADATA_FIELDS.UPDATED_AT,
        }),
        ...(variant === "Processor" && currentCustomer.customerKind !== "enterprise"
          ? [processorColumn({ headerName: t("RegistrationGrid.Connected.Processor.Header") })]
          : []),
        connectedTenantColumn({
          headerName: t(`RegistrationGrid.${variant}.ConnectedTenantHeader`),
          width: variant === "Controller" && currentCustomer.customerKind !== "enterprise" ? 360 : 240,
        }),
        responsibleColumn({
          headerName: t("RegistrationGrid.ResponsibleHeader"),
          noResponsible: t("RegistrationGrid.NoResponsible"),
        }),
        usersWithAccessColumn({ headerName: t("RegistrationGrid.UsersWithAccessHeader") }),
        ...(variant === "Controller" ? [dpiaColumn(t)] : []),
        ...questionCardColumns,
      ]}
      NoRowsOverlay={NoRegistrationOverlay}
      sidePanelProps={{
        panelGroups: [
          {
            mainPanelKey: "overview",
            subPanelKeys: [...REGISTRATION_STATUS_VARIANTS, ...RISK_TAGS],
            renderTabIcon: iconProps => <LayoutDashboardIcon {...iconProps} />,
            tabLabel: t("RegistrationGrid.SidePanels.Overview.TabLabel"),
          },
        ],
        renderContent: ({ activePanelKey, activePanelGroup }) => (
          <RegistrationSidePanel
            activePanelKey={activePanelKey}
            activeSidePanelGroup={activePanelGroup}
            data={visibleRegistrations}
            form={gridForm}
          />
        ),
      }}
      texts={{ searchFieldPlaceholder: t("RegistrationGrid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <RegistrationActionMenu
          registrationId={row.registrationId}
          onDeleted={() => {
            if (selectedRowId === row.registrationId) navigate(paths.registrationsGrid({ variant }));
          }}
          onCopied={newRegistrationId => navigate(paths.registrationsInline({ id: newRegistrationId, variant }))}
        />
      )}
    />
  );
};

const getRowId = (row: GridViewRegistration) => row.registrationId;

const RegistrationGridFooter = () => <DynamicGrid.Footer numberOfRowsTextKey="RegistrationGrid.NumberOfRows" />;

const NoRegistrationOverlay = () => {
  return <EmptyState labelKey="RegistrationGrid.NoRows" />;
};

const VISIBLE_TENANTS_IN_GRID = 2;

const connectedTenantColumn = ({
  headerName,
  width,
}: {
  headerName: string;
  width: number;
}): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: REGISTRATION_METADATA_FIELDS.CONNECTED_TENANT }),
  width,
  valueGetter: (_, row) => connectedTenantsText(row.connectedTenants),
  renderCell: ({ row }) => <ConnectedTenantsCell tenants={row.connectedTenants} />,
});

const ConnectedTenantsCell = ({ tenants }: { tenants: GridViewRegistration["connectedTenants"] }) => {
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();

  return (
    <DynamicGrid.Cell withExtraSpacingTop={false}>
      <DynamicGrid.LimitVisibleItems
        items={tenants}
        numberOfVisibleItems={VISIBLE_TENANTS_IN_GRID}
        stacked
        renderItem={tenant => (
          <Box key={tenant.id} sx={{ display: "flex", alignItems: "center", minHeight: theme => theme.spacing(4) }}>
            <ConnectedTenantEntry tenant={tenant}>
              {currentCustomer.customerKind !== "enterprise" &&
                variant === "Controller" &&
                tenant.personalDataController && (
                  <TenantControllerName>{tenant.personalDataController.name}</TenantControllerName>
                )}
            </ConnectedTenantEntry>
          </Box>
        )}
      />
    </DynamicGrid.Cell>
  );
};

const processorColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({
    headerName,
    field: REGISTRATION_PROCESSOR_METADATA_FIELDS.CONNECTED_PERSONAL_DATA_CONTROLLER,
  }),
  width: 240,
  valueGetter: (_, row) => processorOf(row)?.name ?? "",
  renderCell: ({ row }) => <ProcessorCell processor={processorOf(row)} />,
});

const ProcessorCell = ({ processor }: { processor: ConnectedTenant["personalDataController"] }) => {
  const { paths } = usePrivacyNavigate();
  if (!processor) return null;

  return (
    <DynamicGrid.Cell>
      <DynamicShared.Link
        Icon={TenantIcon3}
        to={paths.pdcInline(processor.id)}
        onClick={() => trackEvent.navigationCellClickWithinRegistrationGrid("personal-data-controllers")}
      >
        {processor.name}
      </DynamicShared.Link>
    </DynamicGrid.Cell>
  );
};

const usersWithAccessColumn = ({ headerName }: { headerName: string }): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: REGISTRATION_METADATA_FIELDS.USERS_WITH_ACCESS }),
  width: 180,
  valueGetter: (_, row) => row.numberOfUsersWithAccess,
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      {!row.managedBy && (
        <UsersWithAccessCell
          registrationId={row.registrationId}
          numberOfUsersWithAccess={row.numberOfUsersWithAccess}
        />
      )}
    </DynamicGrid.Cell>
  ),
});

const UsersWithAccessCell: FC<{ registrationId: string; numberOfUsersWithAccess: number }> = ({
  registrationId,
  numberOfUsersWithAccess,
}) => {
  const variant = useRegistrationVariant();
  const { paths } = usePrivacyNavigate();
  return (
    <DynamicGrid.OpenSubDrawerLink
      to={paths.registrationsUsersWithAccess({ id: registrationId, variant })}
      label={numberOfUsersWithAccess}
      Icon={PersonIcon3}
    />
  );
};

const dpiaColumn = (t: (translation: TranslationKey) => string): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName: t("Dpia.Grid.Title"), field: REGISTRATION_METADATA_FIELDS.DPIA_STATUS }),
  valueGetter: (_, row) => (row.dpiaStatus ? t(`RegistrationDrawer.Dpia.${row.dpiaStatus}`) : ""),
  width: 250,
  renderCell: ({ row }) => (
    <DynamicGrid.Cell>
      <DpiaCell row={row} />
    </DynamicGrid.Cell>
  ),
});

const DpiaCell: FC<{ row: GridViewRegistration }> = ({ row }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const currentOrganisation = useCurrentOrganisation();
  const dpiasQuery = useGetDpias({ enabled: currentOrganisation.dpiaEnabled });
  const { popoverProps, anchorProps } = useHoverPopover();
  const variant = useRegistrationVariant();
  const { paths } = usePrivacyNavigate();

  const openSubDrawerLink = (
    <DynamicGrid.OpenSubDrawerLink
      to={paths.registrationsDpiaRecommended({ id: row.registrationId, variant })}
      label={row.dpiaStatus ? t(`RegistrationDrawer.Dpia.${row.dpiaStatus}`) : undefined}
      Icon={DpiaIcon3}
    />
  );

  const connectedDpia = dpiasQuery.data?.find(dpia => dpia.id === row.connectedDpiaId);

  if (connectedDpia && hasPermission("nova-forms-api:dpia:read")) {
    return (
      <>
        <HoverPopover {...popoverProps}>
          <Stack spacing={1} sx={{ p: 4, maxWidth: "20rem" }}>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1,
                fontSize: theme => theme.typography.pxToRem(12),
                textWrap: "balance",
                whiteSpace: "pre-wrap",
              }}
            >
              <DynamicShared.Link Icon={DpiaIcon3} to={paths.dpiaById(connectedDpia.id)}>
                {t("RegistrationDrawer.Dpia.GoToConnectedDpia")}
              </DynamicShared.Link>
            </Typography>
          </Stack>
        </HoverPopover>
        <Box {...anchorProps}>{openSubDrawerLink}</Box>
      </>
    );
  }

  return openSubDrawerLink;
};

const managedByColumn = ({
  headerName,
  organisationName,
}: {
  headerName: string;
  organisationName: string;
}): GridColDef<GridViewRegistration> => ({
  ...DynamicGrid.requiredColDef({ headerName, field: MANAGED_BY_FIELD }),
  width: 280,
  valueGetter: (_, row) =>
    row.managedBy
      ? `${row.managedBy}${row.managedByResponsible ? ` (${row.managedByResponsible})` : ""}`
      : organisationName,
  renderCell: ({ row }) => (
    <Stack sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), overflowWrap: "anywhere" }}>
        {row.managedBy ?? organisationName}
      </Typography>
      {row.managedBy && row.managedByResponsible && (
        <Typography
          sx={{ fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary", overflowWrap: "anywhere" }}
        >
          {row.managedByResponsible}
        </Typography>
      )}
    </Stack>
  ),
});

export const registrationsGridPageRoutes = (variant: RegistrationVariant) =>
  DynamicGrid.setupRoutes({
    gridElement: <RegistrationsGridPage variant={variant} />,
    rowDrawerRoutes: [
      {
        index: true,
        element: (
          <OrCustomerManaged
            ordinary={<RegistrationInlineFormDrawer />}
            customerManaged={<CustomerManagedRegistrationDrawer />}
          />
        ),
        loader: ({ params }) =>
          void prefetchRegistrationUsersWithAccessCount(DynamicGrid.selectedRowIdFromParams(params), variant),
      },
      {
        path: "guided/*",
        element: (
          <OrCustomerManaged ordinary={<RegistrationMultiStepFormDrawer />} customerManaged={<RedirectToOverview />} />
        ),
      },
      { path: "history", element: <RegistrationHistoryDrawer /> },
      {
        path: "users-with-access",
        element: <OrCustomerManaged ordinary={<UsersWithAccessSubDrawer />} customerManaged={<RedirectToOverview />} />,
      },
      {
        path: "dpia-recommended",
        element: (
          <OrCustomerManaged
            ordinary={<DpiaRecommendedSubDrawer />}
            customerManaged={<DpiaRecommendedSubDrawer isReadOnly />}
          />
        ),
      },
    ],
    gridPrefetcher: () => prefetchRegistrationsGridViewAndForm(variant),
    rowDrawerDefaultPrefetcher: ({ rowId }) => void prefetchRegistrationById(rowId, variant),
  });

export const prefetchRegistrationsGridViewAndForm = (variant: RegistrationVariant) => {
  prefetchRegistrationsGridView(variant);
  prefetchRegistrationForm(variant);
};
